import React from 'react';
import { useTranslation } from 'react-i18next';
import { Opportunity } from '../../types';
import { Trophy, Euro, TrendingUp, Calendar } from 'lucide-react';

interface RecentWinsProps {
  opportunities: Opportunity[];
}

const RecentWins: React.FC<RecentWinsProps> = ({ opportunities }) => {
  const { t, i18n } = useTranslation('dashboard');
  
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  
  const recentWins = opportunities.filter(o => 
    o.stage === 'won' && 
    o.actualCloseDate && 
    new Date(o.actualCloseDate) >= thirtyDaysAgo
  );

  // NOTE: Showing won opportunity values for context/display only
  // Actual revenue is tracked via customer payment schedules to prevent double-counting
  const totalRevenue = recentWins.reduce((sum, o) => sum + (o.value || 0), 0);
  const avgDealSize = recentWins.length > 0 ? totalRevenue / recentWins.length : 0;

  // Previous 30 days for comparison
  const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
  const previousWins = opportunities.filter(o => 
    o.stage === 'won' && 
    o.actualCloseDate && 
    new Date(o.actualCloseDate) >= sixtyDaysAgo &&
    new Date(o.actualCloseDate) < thirtyDaysAgo
  );

  const previousRevenue = previousWins.reduce((sum, o) => sum + (o.value || 0), 0);
  const changePercent = previousRevenue > 0 
    ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 
    : totalRevenue > 0 ? 100 : 0;

  const formatCurrency = (amount: number) => {
    const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="bg-gradient-to-br from-success to-success rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 hover:shadow-card transition-shadow duration-200">
      <div className="flex items-center space-x-2 mb-4">
        <div className="p-2 bg-success rounded-lg">
          <Trophy className="w-5 h-5 text-txt-inverted" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-txt">{t('recentWins.title')}</h3>
          <p className="text-sm text-txt-secondary">{t('stats.vsPrev')}</p>
        </div>
      </div>

        <div className="space-y-4">
          {/* Deals Won */}
          <div className="flex items-center justify-between p-4 bg-bg-card rounded-xl border border-success">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-success-soft rounded-lg">
                <Trophy className="w-5 h-5 text-success" />
              </div>
              <div>
                <div className="text-2xl font-bold text-txt">{recentWins.length}</div>
                <div className="text-xs text-txt-secondary">{t('recentWins.won')}</div>
              </div>
            </div>
          </div>

          {/* Revenue */}
          <div className="flex items-center justify-between p-4 bg-bg-card rounded-xl border border-success">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-success-soft rounded-lg">
                <Euro className="w-5 h-5 text-success" />
              </div>
              <div>
                <div className="text-2xl font-bold text-txt">{formatCurrency(totalRevenue)}</div>
                <div className="text-xs text-txt-secondary">{t('recentWins.value')}</div>
              </div>
            </div>
            {changePercent !== 0 && (
              <div className={`flex items-center space-x-1 text-sm font-semibold ${changePercent > 0 ? 'text-success' : 'text-danger'}`}>
                <TrendingUp className="w-4 h-4" />
                <span>{changePercent > 0 ? '+' : ''}{changePercent.toFixed(0)}%</span>
              </div>
            )}
          </div>

          {/* Avg Deal Size */}
          {recentWins.length > 0 && (
            <div className="p-3 bg-bg-surface rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-txt-secondary">Ø Deal:</span>
                <span className="font-bold text-txt">{formatCurrency(avgDealSize)}</span>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

export default RecentWins;
